import {
	BIRTH_NUMBER_MIN_LENGTH,
	BIRTH_NUMBER_MAX_LENGTH,
	BIRTH_NUMBER_REGEX,
	CONTROL_DIGIT_INDEX,
	NUMBER_WITHOUT_CONTROL_END,
	BIRTH_NUMBER_DIVISOR,
	CONTROL_DIGIT_SPECIAL_CASE,
	YEAR_SLICE_START,
	YEAR_SLICE_END,
	MONTH_SLICE_START,
	MONTH_SLICE_END,
	DAY_SLICE_START,
	DAY_SLICE_END,
	RAW_YEAR_LIMIT,
	YEAR_OFFSET_WITH_CONTROL_LOWER,
	YEAR_OFFSET_WITH_CONTROL_UPPER,
	YEAR_OFFSET_WITHOUT_CONTROL_LOWER,
	YEAR_OFFSET_WITHOUT_CONTROL_UPPER,
	DAY_MIN,
	DAY_MAX,
	MIN_VALID_YEAR,
} from '../../const/zod/zod';

import { getCountedMonth } from './getCountedMonth';

export type BirthNumberValidationResult = {
	isValid: boolean;
	errors: {
		invalidLength?: boolean;
		invalidFormat?: boolean;
		invalidControlDigit?: boolean;
		invalidBirthDate?: boolean;
		hasSlash?: boolean;
	};
};

export const validateBirthNumber = (
	nationalIdentifierNumber: string,
): BirthNumberValidationResult => {
	const errors: BirthNumberValidationResult['errors'] = {};

	if (nationalIdentifierNumber.includes('/')) {
		errors.hasSlash = true;
		nationalIdentifierNumber = nationalIdentifierNumber.replace('/', '');
	}

	const nationalIdentifierNumberLength = nationalIdentifierNumber.length;

	const hasValidLength =
		nationalIdentifierNumberLength >= BIRTH_NUMBER_MIN_LENGTH &&
		nationalIdentifierNumberLength <= BIRTH_NUMBER_MAX_LENGTH;

	if (!hasValidLength) {
		errors.invalidLength = true;
	}

	if (!BIRTH_NUMBER_REGEX.test(nationalIdentifierNumber)) {
		errors.invalidFormat = true;
	}

	if (nationalIdentifierNumber.length === BIRTH_NUMBER_MAX_LENGTH) {
		const controlDigit = parseInt(
			nationalIdentifierNumber.charAt(CONTROL_DIGIT_INDEX),
		);
		const numberWithoutControl = parseInt(
			nationalIdentifierNumber.slice(0, NUMBER_WITHOUT_CONTROL_END),
		);
		const rawModulo = numberWithoutControl % BIRTH_NUMBER_DIVISOR;
		const computedModulo =
			rawModulo === CONTROL_DIGIT_SPECIAL_CASE ? 0 : rawModulo;
		if (computedModulo !== controlDigit) {
			errors.invalidControlDigit = true;
		}
	}

	const rawYear = parseInt(
		nationalIdentifierNumber.slice(YEAR_SLICE_START, YEAR_SLICE_END),
	);
	const rawMonth = parseInt(
		nationalIdentifierNumber.slice(MONTH_SLICE_START, MONTH_SLICE_END),
	);
	const rawDay = parseInt(
		nationalIdentifierNumber.slice(DAY_SLICE_START, DAY_SLICE_END),
	);

	const hasControlDigit =
		nationalIdentifierNumber.length === BIRTH_NUMBER_MAX_LENGTH;
	const isRawYearLessThanLimit = rawYear < RAW_YEAR_LIMIT;

	const yearOffsetWithControl = isRawYearLessThanLimit
		? YEAR_OFFSET_WITH_CONTROL_LOWER
		: YEAR_OFFSET_WITH_CONTROL_UPPER;

	const yearOffsetWithoutControl = isRawYearLessThanLimit
		? YEAR_OFFSET_WITHOUT_CONTROL_LOWER
		: YEAR_OFFSET_WITHOUT_CONTROL_UPPER;

	const yearOffset = hasControlDigit
		? yearOffsetWithControl
		: yearOffsetWithoutControl;

	const countedYear = rawYear + yearOffset;
	const countedMonth = getCountedMonth(rawMonth, countedYear);
	const countedDay = rawDay;

	const birthDate = new Date(countedYear, countedMonth - 1, countedDay);

	const isCountedDayInvalid =
		countedDay < DAY_MIN || countedDay > DAY_MAX || isNaN(countedDay);

	const isBirthDateTimeNan = isNaN(birthDate.getTime());
	const isBirthDateYearLowerThanMin = birthDate.getFullYear() < MIN_VALID_YEAR;
	const isBirthDateMonthDifferentThanCountedMonth =
		birthDate.getMonth() + 1 !== countedMonth;
	const isBirthDateDayDifferentThanCountedDay =
		birthDate.getDate() !== countedDay;

	const invalidBirthNumberCases: boolean[] = [
		isCountedDayInvalid,
		isBirthDateTimeNan,
		isBirthDateYearLowerThanMin,
		isBirthDateMonthDifferentThanCountedMonth,
		isBirthDateDayDifferentThanCountedDay,
	];

	const isBirthDateInvalid = invalidBirthNumberCases.some(Boolean);

	if (isBirthDateInvalid) {
		errors.invalidBirthDate = true;
	}

	const isValid = Object.keys(errors).length === 0;

	return {
		isValid,
		errors,
	};
};
